import Card from 'components/card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductions } from 'interfaces/productions';
import { getProductsByIds } from 'interfaces/products';
import { useDisclosure } from '@chakra-ui/hooks';
import ProductionPreviewModal from './components/ProductionPreview';
import GoBack from 'components/actions/GoBack';
import ProductSummary from 'views/orders/listOrders/components/productsSummary';
import OrderTable from './components/OrderTable';
import { getPaginatedOrders } from 'interfaces/order';
import InputField from 'components/fields/InputField';
import SaveStatus from 'components/actions/SaveStatus';
import { MdRefresh } from 'react-icons/md';

const PrepareProduction = () => {
    const navigate = useNavigate();
    const { isOpen, onClose } = useDisclosure();
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState(
        new Date().toISOString().slice(0, 10),
    );

    const [product] = useState<any>({});
    const [items, setItems] = useState<any[]>(null);
    const [defaultItems, setDefaultItems] = useState<any[]>([]);
    const [orders, setOrders] = useState<any[]>(null);
    const [saveStatus, setSaveStatus] = useState('none');
    const [products, setProducts] = useState<any[]>(null);
    const [productions, setProductions] = useState<any[]>(null);
    const [filter, setFilter] = useState('all');

    const initProductions = async () => {
        const prods = await getProductions();
        // A 403 here (account without production access) isn't a dead
        // session — don't force a logout, just show an empty state.
        if (!prods) {
            setProductions([]);
            return;
        }
        setProductions(prods.data);
    };

    const initProducts = async (productIds: string[]) => {
        if (productIds.length === 0) return;
        const prods = await getProductsByIds([...new Set(productIds)]);
        if (prods) setProducts(prods);
    };

    const onProduction = async (prod: any) => {
        setProductions([prod, ...productions]);
        onClose();
    };

    function mergeOrderItems(orders: any) {
        const mergedItemsMap = new Map();

        orders.forEach((order: any) => {
            order.items.forEach((item: any) => {
                const productId = item.productId;
                if (mergedItemsMap.has(productId)) {
                    const existingItem = mergedItemsMap.get(productId);

                    existingItem.quantity =
                        parseFloat(existingItem.quantity) +
                        parseFloat(item.quantity);
                    if (!order.isReady) {
                        existingItem.quantityToProduce =
                            parseFloat(existingItem.quantityToProduce) +
                            parseFloat(item.quantity);
                    }
                    existingItem.brutWeight += item.brutWeight;
                    existingItem.netWeight += item.netWeight;
                    existingItem.orderIds.push(order._id);
                    existingItem.companies.push(
                        order.displayName || order.company,
                    );
                    existingItem.quantities.push(parseInt(item.quantity));
                    existingItem.isReady.push(order.isReady);
                    existingItem.isItemPrepared.push(item.isPrepared);
                    existingItem.comment.push(item.comment || 'N/A');
                    if (
                        !existingItem.deliveryDates.includes(order.deliveryDate)
                    ) {
                        existingItem.deliveryDates.push(order.deliveryDate);
                    }
                } else {
                    mergedItemsMap.set(productId, {
                        ...item,
                        quantity: parseFloat(item.quantity),
                        quantityToProduce: order.isReady
                            ? 0
                            : parseFloat(item.quantity),
                        defaultLotNumber: item.lotNumber,
                        productId: item.productId,
                        orderIds: [order._id],
                        companies: [order.displayName || order.company],
                        quantities: [parseFloat(item.quantity)],
                        isReady: [order.isReady],
                        isItemPrepared: [item.isPrepared],
                        comment: [item.comment || 'N/A'],
                        deliveryDates: [order.deliveryDate],
                        display: false,
                        _id: item.productId,
                        productTag: item.productTag?.color,
                    });
                }
            });
        });

        return Array.from(mergedItemsMap.values());
    }

    const initOrders = async () => {
        if (!date || date === undefined) return;
        if (!endDate || endDate === undefined) return;
        const res = await getPaginatedOrders(1, 500, 'pending', '', 'all');
        if (res) {
            // date <= order.deliveryDate <= endDate
            const orders = res.data.filter(
                (order: any) =>
                    order.status !== 'draft' &&
                    new Date(order.deliveryDate).toISOString().split('T')[0] >=
                        date &&
                    new Date(order.deliveryDate).toISOString().split('T')[0] <=
                        endDate,
            );

            setOrders(orders);
            const items = mergeOrderItems(orders);
            setItems(items);
            setDefaultItems([...items]);
            // Load only the products referenced in these order items
            initProducts(items.map((i: any) => i.productId).filter(Boolean));
        }
    };

    useEffect(() => {
        setItems(null);
        initOrders();
    }, [date, endDate]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initProductions();
        // initProducts is called inside initOrders once we know which product IDs are needed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ProductionPreviewModal
                isOpen={isOpen}
                onClose={onClose}
                product={product}
                productName={
                    products?.find((p) => p._id === product.product)?.name
                }
                onProduction={onProduction}
            />
            <div className="mt-2">
                <GoBack />
            </div>
            <div className="mt-2 grid  grid-cols-12 gap-5">
                <div className="col-span-12">
                    <ProductSummary />
                </div>
                <div className="col-span-12">
                    <Card extra={'mt-1 w-full px-8 border'}>
                        <div className="absolute right-3 top-2">
                            <SaveStatus status={saveStatus} />
                        </div>
                        <div className="absolute right-2 top-3">
                            <MdRefresh
                                className="cursor-pointer"
                                onClick={initOrders}
                            />
                        </div>
                        <div className="h-full w-full">
                            <div>
                                <h4 className="mt-8 sm:mt-2 text-lg font-medium text-navy-700 dark:text-white">
                                    Produits commandés
                                </h4>
                                <div className="flex my-auto mx-auto">
                                    <InputField
                                        label=""
                                        placeholder="01/01/2021"
                                        id="deliveryDate"
                                        type="date"
                                        extra="w-[140px] mx-2"
                                        value={date}
                                        onChange={(e: any) =>
                                            setDate(e.target.value)
                                        }
                                        inputSmall={true}
                                    />
                                    <p className="flex my-auto"> - </p>
                                    <InputField
                                        label=""
                                        placeholder="01/01/2021"
                                        id="deliveryDate"
                                        type="date"
                                        extra="w-[140px] mx-2"
                                        value={endDate}
                                        onChange={(e: any) =>
                                            setEndDate(e.target.value)
                                        }
                                        inputSmall={true}
                                    />
                                    <div className="ml-4 flex my-auto gap-4">
                                        <p
                                            className={`text-xs ${
                                                filter === 'all'
                                                    ? 'text-toola-500 underline'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer`}
                                            onClick={() => setFilter('all')}
                                        >
                                            Tout afficher
                                        </p>
                                        <p
                                            className={`text-xs ${
                                                filter === 'to-prepare'
                                                    ? 'text-toola-500 underline'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer`}
                                            onClick={() =>
                                                setFilter('to-prepare')
                                            }
                                        >
                                            À préparer
                                        </p>
                                        <p
                                            className={`text-xs ${
                                                filter === 'prepared'
                                                    ? 'text-toola-500 underline'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer`}
                                            onClick={() =>
                                                setFilter('prepared')
                                            }
                                        >
                                            Déjà préparé
                                        </p>
                                        {/* <p
                                            className={`text-xs ${
                                                filter === 'associated'
                                                    ? 'text-toola-500 underline'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer`}
                                            onClick={() =>
                                                setFilter('associated')
                                            }
                                        >
                                            Nº de lot associé
                                        </p>
                                        <p
                                            className={`text-xs ${
                                                filter === 'not-associated'
                                                    ? 'text-toola-500 underline'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer`}
                                            onClick={() =>
                                                setFilter('not-associated')
                                            }
                                        >
                                            Nº de lot non associé
                                        </p> */}
                                    </div>
                                </div>
                                {items ? (
                                    <>
                                        <>
                                            {items && (
                                                <OrderTable
                                                    items={items}
                                                    products={products}
                                                    defaultItems={defaultItems}
                                                    filter={filter}
                                                    handleQtyChange={null}
                                                    handlePriceChange={null}
                                                    handleDiscountChange={null}
                                                    handleCommentChange={null}
                                                    handleDiscountTypeChange={
                                                        null
                                                    }
                                                    setSaveStatus={
                                                        setSaveStatus
                                                    }
                                                    onDelete={null}
                                                />
                                            )}
                                        </>
                                    </>
                                ) : (
                                    <div className="my-10 justify-center">
                                        <div className="flex justify-center items-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                                        </div>
                                        <p className="mt-2 flex text-xs justify-center text-gray-900">
                                            chargement...
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default PrepareProduction;
